import { useState } from "react";
import { Box, Button, Group, Modal, Stack, Text, TextInput } from "@mantine/core";
import { Dropzone } from "@mantine/dropzone";
import { IconUpload } from "@tabler/icons-react";
import { mutateFontCreate } from "@/Services";
import { useTranslation } from "react-i18next";

const ALLOWED_EXTENSIONS = ["ttf", "otf", "woff", "woff2"];
const PREVIEW_TEXT = "AaBbCc 123";

const extensionOf = (filename) => (filename.split(".").pop() || "").toLowerCase();

export const FontUploadModal = ({ opened, onClose, onUploaded }) => {

    const { t } = useTranslation();
    const [file, setFile] = useState(null);
    const [dataUri, setDataUri] = useState("");
    const [name, setName] = useState("");
    const [previewFamily, setPreviewFamily] = useState("");
    const [previewReady, setPreviewReady] = useState(false);
    const [error, setError] = useState("");

    const mutateCreate = mutateFontCreate();

    const reset = () => {
        setFile(null);
        setDataUri("");
        setName("");
        setPreviewFamily("");
        setPreviewReady(false);
        setError("");
    };

    const handleClose = () => {
        reset();
        onClose();
    };

    const handleDrop = (files) => {
        const droppedFile = files[0];
        if (!droppedFile) return;

        const extension = extensionOf(droppedFile.name);
        if (!ALLOWED_EXTENSIONS.includes(extension)) {
            setError(t('font.invalidExtension'));
            return;
        }

        setError("");
        setFile(droppedFile);
        setName(droppedFile.name.replace(/\.[^.]+$/, ""));
        setPreviewReady(false);

        const reader = new FileReader();
        reader.onload = () => {
            setDataUri(reader.result);

            if (typeof FontFace === "undefined") {
                setPreviewReady(true);
                return;
            }

            const blobUrl = URL.createObjectURL(droppedFile);
            const familyName = `font-preview-${Date.now()}`;
            const fontFace = new FontFace(familyName, `url(${blobUrl})`);

            fontFace.load()
                .then((loaded) => {
                    document.fonts.add(loaded);
                    setPreviewFamily(familyName);
                    setPreviewReady(true);
                })
                .catch(() => {
                    setError(t('font.invalidFile'));
                })
                .finally(() => URL.revokeObjectURL(blobUrl));
        };
        reader.readAsDataURL(droppedFile);
    };

    const handleUpload = () => {
        if (!dataUri || !name.trim()) return;

        mutateCreate.mutate({ name: name.trim(), data: dataUri }, {
            onSuccess: ({ data }) => {
                onUploaded?.(data);
                handleClose();
            },
        });
    };

    return (
        <Modal opened={opened} onClose={handleClose} title={t('font.uploadFont')} radius="lg" centered>
            <Stack gap="md">
                {!file ? (
                    <Dropzone onDrop={handleDrop} maxSize={2 * 1024 * 1024} maxFiles={1} multiple={false} radius="md">
                        <Group justify="center" gap="xl" style={{ pointerEvents: "none" }}>
                            <IconUpload size={24} stroke={1.5} />
                            <Stack align="center" gap={2}>
                                <Text size="sm" c="dimmed">{t('upload.clickOrDrag')}</Text>
                                <Text size="xs" c="dimmed">{t('font.accepted')}</Text>
                            </Stack>
                        </Group>
                    </Dropzone>
                ) : (
                    <>
                        <TextInput
                            variant="filled"
                            label={t('font.familyName')}
                            description={t('font.familyNameDescription')}
                            value={name}
                            onChange={(event) => setName(event.currentTarget.value)}
                        />
                        <Box p="md" style={{ border: '1px solid var(--mantine-color-gray-3)', borderRadius: 'var(--mantine-radius-md)' }}>
                            <Text fz={12} c="dimmed" mb={4}>{t('common.preview')}</Text>
                            <Text fz={28} fw={700} style={previewReady ? { fontFamily: previewFamily } : undefined}>
                                {previewReady ? PREVIEW_TEXT : t('font.loadingPreview')}
                            </Text>
                        </Box>
                        <Button variant="subtle" size="xs" onClick={reset}>{t('font.chooseDifferent')}</Button>
                    </>
                )}

                {error && <Text c="red" fz="sm">{error}</Text>}

                <Group justify="flex-end">
                    <Button variant="outline" color="gray" radius="md" onClick={handleClose}>{t('common.cancel')}</Button>
                    <Button
                        radius="md"
                        onClick={handleUpload}
                        loading={mutateCreate.isPending}
                        disabled={!dataUri || !name.trim() || !previewReady}
                    >
                        {t('font.uploadFont')}
                    </Button>
                </Group>
            </Stack>
        </Modal>
    );
};
